import { useState } from "react"
import { NavLink, useLocation } from "react-router-dom"
import { useClientTheme } from "@/app/components/ThemeContext"
import type { CapabilityKey } from "@/app/lib/capabilities"

export interface NavItem {
  path: string
  label: string
  icon: React.ReactNode
  group?: string
  // When set, the item is hidden unless the client has this capability
  // enabled (see @/app/lib/capabilities). Server remains authoritative —
  // this is UX, not security.
  capability?: CapabilityKey
}

interface SidebarNavProps {
  items: NavItem[]
  // Called after a nav item is tapped — used by the mobile drawer to close itself.
  onNavigate?: () => void
}

// Shared nav list, rendered both in the desktop sidebar and the mobile drawer.
export function SidebarNav({ items, onNavigate }: SidebarNavProps) {
  const { theme } = useClientTheme()
  const location = useLocation()
  const [hoveredPath, setHoveredPath] = useState<string | null>(null)

  let lastGroup: string | undefined

  return (
    <>
      {/* Nav section label — 24px left edge aligns with the pill's inner padding */}
      <div className="px-6 pt-5 pb-2">
        <span
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: "10px",
            fontWeight: 600,
            color: theme.textDim,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
          }}
        >
          Navigation
        </span>
      </div>

      <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
        {items.map((item) => {
          const showGroup = item.group && item.group !== lastGroup
          if (item.group) lastGroup = item.group

          return (
            <div key={item.path}>
              {showGroup && (
                <div className="px-3 pt-4 pb-1">
                  <span
                    style={{
                      fontFamily: "var(--font-ui)",
                      fontSize: "10px",
                      fontWeight: 600,
                      color: theme.textDim,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      transition: "color 350ms ease",
                    }}
                  >
                    {item.group}
                  </span>
                </div>
              )}
              <NavLink
                to={item.path}
                onClick={onNavigate}
                onMouseEnter={() => setHoveredPath(item.path)}
                onMouseLeave={() => setHoveredPath(null)}
                style={({ isActive }) => ({
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  width: "100%",
                  color: isActive ? theme.sidebarActiveText : theme.sidebarInactiveText,
                  fontFamily: "var(--font-ui)",
                  fontSize: "13px",
                  fontWeight: isActive ? 600 : 400,
                  textDecoration: "none",
                  // font-weight animates because DM Sans loads as a variable font.
                  transition: "color 350ms ease, font-weight 350ms ease",
                })}
              >
                {({ isActive }) => (
                  <>
                    {/* Hover wash for inactive items, under the active pill. */}
                    <span
                      aria-hidden
                      style={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: "8px",
                        backgroundColor: theme.bgSurface2,
                        opacity: !isActive && hoveredPath === item.path ? 1 : 0,
                        transition: "opacity 150ms ease",
                        pointerEvents: "none",
                      }}
                    />
                    {/* Active pill on its own layer so selection fades in/out via
                        opacity instead of snapping with the background swap. */}
                    <span
                      aria-hidden
                      style={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: "8px",
                        backgroundColor: theme.sidebarActiveBg,
                        opacity: isActive ? 1 : 0,
                        transition: "opacity 350ms ease",
                        pointerEvents: "none",
                      }}
                    />
                    <span
                      className="relative flex items-center"
                      style={{
                        opacity: location.pathname === item.path ? 1 : 0.7,
                        transition: "opacity 350ms ease",
                      }}
                    >
                      {item.icon}
                    </span>
                    <span className="relative">{item.label}</span>
                  </>
                )}
              </NavLink>
            </div>
          )
        })}
      </nav>
    </>
  )
}
